import { useEffect, useLayoutEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

/* body is overflow:hidden with a pinned height chain (index.css → #root →
   Main's SiteContent/MainContent), so the window never scrolls: #main is the
   page's one and only scroller. Everything here reads and writes that
   element; window.scrollY is permanently 0 in this app. */
const SCROLLER_ID = "main";

/* Keyed by URL read straight off window.location, deliberately not by
   react-router's location.key. Plenty of this app navigates with raw
   window.history.pushState/replaceState (profile tab clicks, clip actions,
   studio, settings menu), and each of those wipes the {key} that history v4
   stores in window.history.state — so on the way back react-router hands us
   `key: undefined` and any position filed under the old key is unreachable.
   The live URL survives all of it. Trade-off: the same URL at two different
   depths of the stack shares one offset, which is what you'd want anyway. */
const positions = new Map<string, number>();

/* Distinct URLs the viewer scrolled, not bytes — a few dozen is plenty and
   keeps a long session from growing this without bound. */
const MAX_ENTRIES = 40;

/* Content lands well after the route renders — lazy chunk first, then the
   list's query — so the scroller is almost never tall enough to hold the old
   offset on the first frame. Keep reapplying until it is, then give up rather
   than spin forever on a page that legitimately got shorter. */
const RESTORE_TIMEOUT_MS = 2000;

/* Read live rather than from useLocation(): a raw replaceState changes the
   real URL without react-router noticing, so its location can be stale. */
const currentKey = () => window.location.pathname + window.location.search;

const remember = (key: string, top: number) => {
  positions.delete(key);
  positions.set(key, top);
  if (positions.size > MAX_ENTRIES) {
    const oldest = positions.keys().next().value;
    if (oldest !== undefined) positions.delete(oldest);
  }
};

/* Mounted once, inside Main, so #main exists by the time effects run. Owns
   scroll position for every route: fresh navigations start at the top (what
   Main's own effect used to do unconditionally), back/forward restore. */
export const ScrollRestoration = () => {
  const location = useLocation();
  const history = useHistory();
  // Suppresses the scroll listener below while we're the ones scrolling —
  // otherwise each intermediate scrollTo() overwrites the very offset we're
  // trying to reach, and the restore walks itself back to the top.
  const restoringRef = useRef(false);

  useEffect(() => {
    const scroller = document.getElementById(SCROLLER_ID);
    if (!scroller) return;

    let frame = 0;
    const onScroll = () => {
      if (restoringRef.current) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        remember(currentKey(), scroller.scrollTop),
      );
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Layout effect, not useEffect: when the popped page first commits it is
  // shorter than the offset we're about to restore, and the browser clamps
  // scrollTop and fires a scroll event for it. That event must find
  // restoringRef already set, or it overwrites the saved offset with the
  // clamped one before we've read it.
  useLayoutEffect(() => {
    const scroller = document.getElementById(SCROLLER_ID);
    if (!scroller) return;

    const target =
      history.action === "POP" ? positions.get(currentKey()) : undefined;

    if (!target) {
      scroller.scrollTo(0, 0);
      return;
    }

    const startedAt = Date.now();
    let cancelled = false;
    restoringRef.current = true;

    const stop = () => {
      cancelled = true;
      restoringRef.current = false;
      scroller.removeEventListener("wheel", stop);
      scroller.removeEventListener("touchmove", stop);
      window.removeEventListener("keydown", stop);
    };

    // Any deliberate scroll input wins — never fight the viewer for control.
    scroller.addEventListener("wheel", stop, { passive: true });
    scroller.addEventListener("touchmove", stop, { passive: true });
    window.addEventListener("keydown", stop);

    const tick = () => {
      if (cancelled) return;

      const maxScroll = Math.max(
        scroller.scrollHeight - scroller.clientHeight,
        0,
      );
      scroller.scrollTo(0, Math.min(target, maxScroll));

      if (maxScroll >= target || Date.now() - startedAt > RESTORE_TIMEOUT_MS) {
        stop();
        return;
      }
      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
    return stop;
    // history is a stable mutable object; a new location is what marks a
    // navigation, and history.action is read fresh each time one arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return null;
};
